import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AppProviders } from '@/app/providers/AppProviders';
import { AppRouter } from '@/app/router';
import { runLegacyStorageMigration } from '@/shared/storage/legacyStorageMigration';

// The skin, brought across whole and in the order it was loaded before.
//
// index.css is pre-compiled Tailwind output plus every custom class the ported
// components use -- 14,000 lines, shipped rather than generated. tailwind-jit.css
// is the actual Tailwind entry: it carries `@import "tailwindcss"`, the dark
// variant and the keyframes.
//
// globals.css was copied for reference and is deliberately NOT imported, exactly
// as it was not before. It is the source those 14,000 lines were compiled from,
// and loading it as well means `@apply border-border` against a theme that has
// not been declared -- which is precisely what failed the first build.
//
// All three are refactor targets, not a final arrangement.
import '@/index.css';
import '@/tailwind-jit.css';
import '@/styles/index.css';
import '@/styles/form-feedback.css';
import '@/styles/panel-swap.css';
import '@/styles/autofill.css';
import '@/styles/scrollbars.css';
import '@/styles/dialog.css';

// Before anything reads a storage key: the session, the scoped agency selection
// and the theme were all renamed out of the previous product's namespace on
// 17 October 2026, and a visitor who has not opened the app since then still has
// them under the old names. This copies them across, once, and is a no-op on
// every start after that. The theme is also migrated by the pre-paint script in
// index.html, which is the copy that avoids a flash -- see that script.
//
// It cannot throw, so it is not guarded here.
runLegacyStorageMigration();

const container = document.getElementById('root');

// Explicit rather than a non-null assertion. If the element is missing, the
// message names the cause; an assertion produces "Cannot read properties of
// null" from inside React and sends the reader in the wrong direction.
if (!container) {
  throw new Error('index.html has no #root element to mount into.');
}

createRoot(container).render(
  <StrictMode>
    <AppProviders>
      <AppRouter />
    </AppProviders>
  </StrictMode>,
);
